import {
  useInfiniteQuery,
  UseInfiniteQueryOptions,
  UseQueryOptions,
} from "@tanstack/react-query"
import { AxiosError } from "axios"

import { API_DEFAULT } from "src/constants/minutApi"
import {
  guestKeys,
  useGuestCache,
} from "src/data/guests/queries/guestQueryCache"
import {
  TFetchGuestMessagesResponse,
  TFetchHomeGuestsQueryParams,
  TFetchHomeGuestsResponse,
  TFetchOrganizationGuestsQueryParams,
  TFetchOrganizationGuestsResponse,
} from "src/data/guests/types/guestTypes"
import {
  useMinutDelete,
  useMinutPost,
  useMinutPut,
  useMinutQuery,
} from "src/hooks/minutQuery"
import { minutApiHttpClient } from "src/utils/minutApiHttpClient"

export enum SortOrder {
  ASCENDING = "asc",
  DESCENDING = "desc",
}
export enum SortKey {
  CHECK_IN = "checkinTime",
  CHECK_OUt = "checkoutTime",
}

export function useFetchHomeGuests({
  homeId,
  filters,
  options,
}: {
  homeId: string
  filters?: TFetchHomeGuestsQueryParams
  options?: UseQueryOptions<
    TFetchHomeGuestsResponse,
    AxiosError,
    TFetchHomeGuestsResponse,
    ReturnType<typeof guestKeys.homeList>
  >
}) {
  return useMinutQuery<"/homes/{home_id}/guests">({
    queryKey: guestKeys.homeList(homeId, filters),
    apiPath: "/homes/{home_id}/guests",
    queryPath: `/homes/${homeId}/guests`,
    filters,
    ...options,
  })
}

export function useFetchHomeGuestsInfinite({
  homeId,
  filters,
  options,
}: {
  homeId: string
  filters?: TFetchHomeGuestsQueryParams
  options?: UseInfiniteQueryOptions<
    TFetchHomeGuestsResponse,
    AxiosError,
    TFetchHomeGuestsResponse,
    TFetchHomeGuestsResponse,
    ReturnType<typeof guestKeys.homeListInfinite>
  >
}) {
  async function fetchHomeGuestsInfinite({ pageParam = undefined }) {
    const response = await minutApiHttpClient.get<TFetchHomeGuestsResponse>(
      `${API_DEFAULT}/homes/${homeId}/guests`,
      {
        params: {
          ...filters,
          offset: pageParam,
        },
      }
    )

    return response.data
  }

  return useInfiniteQuery({
    queryKey: guestKeys.homeListInfinite(homeId, filters),
    queryFn: fetchHomeGuestsInfinite,
    getNextPageParam: (lastPage) => {
      const offset = lastPage.paging.offset ?? 0
      const limit = lastPage.paging.limit ?? 0
      const totalCount = lastPage.paging.total_count ?? 0

      if (offset + limit >= totalCount) {
        return undefined
      }

      return offset + limit
    },
    ...options,
  })
}

export function useFetchHomeGuest({
  homeId,
  guestId,
}: {
  homeId: string
  guestId: string
}) {
  return useMinutQuery<"/homes/{home_id}/guests/{guest_id}">({
    queryKey: guestKeys.homeGuest({ homeId, guestId }),
    apiPath: "/homes/{home_id}/guests/{guest_id}",
    queryPath: `/homes/${homeId}/guests/${guestId}`,
  })
}

export function useDeleteGuest({ homeId }: { homeId: string }) {
  const { invalidateGuestList } = useGuestCache()

  return useMinutDelete<"/homes/{home_id}/guests/{guest_id}">({
    apiPath: "/homes/{home_id}/guests/{guest_id}",
    pathFn: ({ home_id, guest_id }) => `/homes/${home_id}/guests/${guest_id}`,
    options: {
      onSuccess: () => {
        invalidateGuestList(homeId)
      },
    },
  })
}

export function usePostGuest() {
  const { invalidateGuestList } = useGuestCache()

  return useMinutPost<"/homes/{home_id}/guests">({
    apiPath: "/homes/{home_id}/guests",
    pathFn: ({ home_id }) => `/homes/${home_id}/guests`,
    options: {
      onSuccess: (data) => {
        invalidateGuestList(data.home_id)
      },
    },
  })
}

export function usePutGuest() {
  const { invalidateGuestList, invalidateGuest } = useGuestCache()

  return useMinutPut<"/homes/{home_id}/guests/{guest_id}">({
    apiPath: "/homes/{home_id}/guests/{guest_id}",
    pathFn: ({ home_id, guest_id }) => `/homes/${home_id}/guests/${guest_id}`,
    options: {
      onSuccess: (data) => {
        invalidateGuestList(data.home_id)
        invalidateGuest(data.home_id, data.guest_id)
      },
    },
  })
}

export function useFetchOrganizationGuests({
  orgId,
  filters,
  options,
}: {
  orgId: string
  filters?: TFetchOrganizationGuestsQueryParams
  options?: {
    refetchInterval?: number
  }
}) {
  return useMinutQuery<"/organizations/{organization_id}/guests">({
    queryKey: guestKeys.organizationList(orgId, filters),
    apiPath: "/organizations/{organization_id}/guests",
    queryPath: `/organizations/${orgId}/guests`,
    options: {
      refetchInterval: options?.refetchInterval,
    },
    filters,
  })
}

export function useFetchOrganizationGuestsInfinite({
  orgId,
  filters,
  options,
}: {
  orgId: string
  filters?: TFetchOrganizationGuestsQueryParams
  options?: UseInfiniteQueryOptions<
    TFetchOrganizationGuestsResponse,
    AxiosError,
    TFetchOrganizationGuestsResponse,
    TFetchOrganizationGuestsResponse,
    ReturnType<typeof guestKeys.organizationListInfinite>
  >
}) {
  async function fetchOrganizationGuestsInfinite({ pageParam = undefined }) {
    const response =
      await minutApiHttpClient.get<TFetchOrganizationGuestsResponse>(
        `${API_DEFAULT}/organizations/${orgId}/guests`,
        {
          params: {
            ...filters,
            offset: pageParam,
          },
        }
      )

    return response.data
  }

  return useInfiniteQuery({
    queryKey: guestKeys.organizationListInfinite(orgId, filters),
    queryFn: fetchOrganizationGuestsInfinite,
    getNextPageParam: (lastPage) => {
      if (
        lastPage.paging.offset + lastPage.paging.limit >=
        lastPage.paging.total_count
      ) {
        return undefined
      }

      return lastPage.paging.offset + lastPage.paging.limit
    },
    ...options,
  })
}

export function useFetchOrganizationGuest({
  orgId,
  guestId,
}: {
  orgId: string
  guestId: string
}) {
  return useMinutQuery<"/organizations/{organization_id}/guests/{guest_id}">({
    queryKey: guestKeys.organizationGuest(orgId, guestId),
    apiPath: "/organizations/{organization_id}/guests/{guest_id}",
    queryPath: `/organizations/${orgId}/guests/${guestId}`,
  })
}

/**
 * This query is used to fetch guest messages in an infinite scroll manner.
 * It is used to fetch the latest messages first, and then the older messages.
 * It is used to fetch the messages in chunks of 50.
 *
 * Internally it reverses the messages to make it easier to fetch the latest messages first.
 */
export function useFetchGuestMessagesInfinite({
  orgId,
  guestId,

  options,
}: {
  orgId: string
  guestId: string
  options?: UseInfiniteQueryOptions<
    TFetchGuestMessagesResponse,
    AxiosError,
    TFetchGuestMessagesResponse,
    TFetchGuestMessagesResponse,
    ReturnType<typeof guestKeys.organizationGuestMessagesInfinite>
  >
}) {
  async function fetchGuestMessagesInfinite({ pageParam = undefined }) {
    const response = await minutApiHttpClient.get<TFetchGuestMessagesResponse>(
      `${API_DEFAULT}/organizations/${orgId}/guests/${guestId}/messages`,
      {
        params: {
          sort_by: "timestamp",
          sort_order: "desc",
          limit: 50,
          offset: pageParam,
        },
      }
    )

    return {
      messages: response.data.messages.toReversed(),
      paging: response.data.paging,
    }
  }

  return useInfiniteQuery({
    queryKey: guestKeys.organizationGuestMessagesInfinite(orgId, guestId),
    queryFn: fetchGuestMessagesInfinite,
    getPreviousPageParam: (lastPage) => {
      if (
        lastPage.paging.offset + lastPage.paging.limit >=
        lastPage.paging.total_count
      ) {
        return undefined
      }

      return lastPage.paging.offset + lastPage.paging.limit
    },
    ...options,
  })
}

export function usePostGuestMessage() {
  const { invalidateGuestMessages } = useGuestCache()

  return useMinutPost<"/organizations/{organization_id}/guests/{guest_id}/messages">(
    {
      pathFn: (variables) =>
        `/organizations/${variables.organization_id}/guests/${variables.guest_id}/messages`,
      options: {
        onSuccess: (_, variables) => {
          invalidateGuestMessages(
            variables.path.organization_id,
            variables.path.guest_id
          )
        },
      },
    }
  )
}
